import { Grid2, Stack, Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import ConversionToggle from '@components/ConversionToggle/ConversionToggle';
import Icon from '@components/Icon/Icon';

import { getObservationConfig } from '@constants/fhir';

import { useHealthChart } from '@hooks/useHealthChart';
import useReferenceRange from '@hooks/useReferenceRange';

import { FRIENDLY_CODE_NAME_MAP } from '@models/graph';

import { getUnits } from '@utils/conversion';
import { displayFriendlyRiskLevel } from '@utils/reference_range';

const messages = defineMessages({
  unit: {
    id: 'healthTracker.unit',
    defaultMessage: 'Unit: {unit}',
  },
});

export default function Range() {
  const { formatMessage } = useIntl();
  const { code, loading, referenceRangeMap } = useHealthChart();
  const { displayFriendlyRange, parameterMap } = useReferenceRange();

  const fallbackUnit = formatMessage(messages.unit, {
    unit: getObservationConfig(code)?.unit,
  });
  const units = getUnits(code);

  return (
    <ContentLoader lineCount={referenceRangeMap.size} loading={loading}>
      {units.SI ? (
        <ConversionToggle group={code} mui type={code} />
      ) : (
        <Typography
          color="text.secondary"
          pl={1}
          textAlign={{ xs: 'center', sm: 'left' }}
          variant="caption"
        >
          {fallbackUnit}
        </Typography>
      )}
      <Stack spacing={1.5}>
        {Array.from(referenceRangeMap.entries()).map(([risk, value]) => {
          const messageId = displayFriendlyRiskLevel(code, risk);
          const title = messageId ? formatMessage({ id: messageId }) : '';

          const range = value.parameters.map((p) => {
            const friendlyRange = displayFriendlyRange({
              code,
              parameters: [p],
              include_units: false,
              client_facing: true,
            });
            const title = FRIENDLY_CODE_NAME_MAP[p.code] || null;
            return (
              <Stack key={`parameter-range-${p.parameter_id}`}>
                {title ? `${title}:` : ''} {friendlyRange}
              </Stack>
            );
          });

          return (
            <Grid2
              container
              key={`reference-range-${risk}`}
              my={2}
              p={0.75}
              sx={{
                border: `1px solid ${parameterMap[risk].colour}`,
                borderRadius: 2,
                backgroundColor: parameterMap[risk].bgColour,
                textAlign: {
                  xs: 'center',
                  sm: 'left',
                },
              }}
            >
              <Grid2 size={{ xs: 12, sm: 4 }}>{range}</Grid2>
              <Grid2
                size={{
                  xs: 12,
                  sm: 'auto',
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: {
                    xs: 'center',
                    sm: 'flex-start',
                  },
                }}
              >
                <Icon
                  color={parameterMap[risk].colour}
                  name="square"
                  size="lg"
                  type="fas"
                />
                <Typography variant="body1" mx={1}>
                  {title}
                </Typography>
              </Grid2>
            </Grid2>
          );
        })}
      </Stack>
    </ContentLoader>
  );
}
